import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  Avatar,
  Button,
  Divider,
  Skeleton,
  Tooltip,
  IconButton,
  Stack,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import CommonLayout from "layouts/DashboardLayout";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { findBlogById } from "../../redux/apis/blogs";
import { useSelector } from "react-redux";
import AngleRightIcon from "common/customIcons/angle_right";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { openSnackbar } from ".././../redux/slices/common";
import colors from "assets/theme/base/colors";
import { getPermissionsByMode } from "utilities/common";
import { hasSectionPermission } from "utilities/common";
import { getData } from "utilities/common";
import StatusText from 'common/component/StatusText'
import dayjs from "dayjs";
import { convertToAmPm } from "utilities/common";

export default function BlogDetailView() {
  const { grey } = colors;
  const { blogId } = useParams();
  const dispatch = useDispatch();
  const { currentBlog, loading } = useSelector(state => state.blogs);
  const [copyStatus, setCopyStatus] = useState('')
  const navigate = useNavigate()

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentBlog?.blog?.canonical)
      .then(() => {
        setCopyStatus('Copied!')
        setTimeout(() => setCopyStatus(''), 1000) // Clear message after 2 seconds
      })
      .catch(() => {
        setCopyStatus('Failed to copy')
        setTimeout(() => setCopyStatus(''), 1000) // Clear message after 2 seconds
      });
  }

  useEffect(() => {
    if (blogId) {
      const fetchBlogDetails = () => {
        try {
          dispatch(findBlogById(blogId));
        } catch (error) {
          dispatch(
            openSnackbar({
              content: error.message || "Failed to fetch blog details",
              color: "error",
              icon: "warning",
            })
          );
        }
      };
      fetchBlogDetails();
    }
  }, [blogId, dispatch]);

  const blogData = useMemo(() => {
    if (!currentBlog?.blog) return {};
    return {
      title: currentBlog.blog.title || "",
      slug: currentBlog.blog.slug || "",
      category: currentBlog.blog.category || "",
      author: currentBlog?.blog?.author || "",
      excerpt: currentBlog.blog.description || "",
      tags: currentBlog.blog.tags || [],
      content: currentBlog.blog.content || [],
      banner: currentBlog.blog.image?.url || "",
      listingImage: currentBlog.blog.listingImage?.url || "",
      metaTitle: currentBlog.blog.metaTitle || "",
      updatedBy: currentBlog.blog.updatedBy || "",
      metaDescription: currentBlog.blog.metaDescription || "",
      faqs: currentBlog.blog.faq || [],
      faqStatus: currentBlog.blog.faqStatus || false,
      status: currentBlog.blog.status || "",
      canonical: currentBlog.blog.canonical || "",
      state: currentBlog.blog.state || "",
      readTime: currentBlog.blog.readTime || "",
      relatedBlogStatus: currentBlog.blog.relatedBlogStatus || false,
      relatedBlogData: currentBlog.blog.relatedBlogData || [],
      scheduleBlogStatus: currentBlog.blog.scheduleBlogStatus || false,
      scheduleDate: currentBlog.blog.scheduleDate || null,
      scheduleTime: currentBlog.blog.scheduleTime || null
    };
  }, [currentBlog]);

  let localUser = getData('login-user-info')
  if (localUser) {
    localUser = JSON.parse(localUser)
  }

  const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "Manage Website", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "Manage Website", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "Manage Website", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Manage Website", "Delete");

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Box display="flex" alignItems="center" justifyContent={'space-between'} mb={2}>
          <Box display="flex" alignItems="center" color='text.main' lineHeight={1}>
            <span style={{ cursor: 'pointer', display: "flex", alignItems: "center" }} onClick={() => { navigate('/manages/blog') }}>
              <AngleRightIcon fontSize="14px" />
              <Typography variant='h6' fontWeight="semibold" ml={.75}>Blog</Typography>
            </span>
          </Box>

          {(blogId && currentBlog?.blog?.canonical) && (
            <Box
              display="flex"
              sx={{ cursor: "pointer" }}
              alignItems="center"
              color="text.main"
              lineHeight={1}
              gap={2}
              style={{ position: "relative" }}
            >
              <Tooltip title={copyStatus ? copyStatus : "Copy"} arrow>
                <IconButton
                  size="small"
                  onClick={handleCopyLink}
                  sx={{ p: 0, color: 'primary.main' }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography
                variant="body"
                component="a"
                href={`${currentBlog.blog?.canonical}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: "none", color: "inherit" }}
                style={{
                  borderRadius: "26px",
                  color: "#0163FF",
                  fontSize: "14px",
                  fontWeight: "400",
                  border: "1px solid #0163FF",
                  padding: "10px 16px",
                }}
              >{`${currentBlog.blog?.canonical}`}</Typography>
            </Box>
          )}
        </Box>

        {loading ? (
          // Skeleton loading state
          <>
            <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
              <Skeleton variant="rectangular" width="60%" height={40} />
              <Grid container spacing={3} mt={2}>
                {[...Array(4)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Skeleton variant="text" width="60%" height={30} />
                    <Skeleton variant="text" width="80%" height={25} />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Skeleton variant="text" width="60%" height={30} />
                  <Skeleton variant="text" width="100%" height={100} />
                </Grid>
              </Grid>
            </Card>

            <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
              <Skeleton variant="text" width="30%" height={40} />
              <Grid container spacing={3} mt={2}>
                {[...Array(3)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Skeleton variant="text" width="60%" height={30} />
                    <Skeleton variant="text" width="80%" height={25} />
                  </Grid>
                ))}
              </Grid>
            </Card>

            <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
              <Skeleton variant="text" width="30%" height={40} />
              <Box mt={3}>
                {[...Array(3)].map((_, i) => (
                  <Box key={i} mb={3}>
                    <Skeleton variant="text" width="80%" height={30} />
                    <Skeleton variant="text" width="100%" height={60} />
                    {i < 2 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))}
              </Box>
            </Card>
          </>
        ) : (
          // Actual content when data is loaded
          <>
            <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" fontWeight="bold">
                  {blogData?.title || "No Title Available"}
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.75}
                  lineHeight={1}
                  p={0.75}
                  borderRadius={2}
                  mr={"auto"}
                >
                  {blogData?.status === "published" && !blogData?.scheduleBlogStatus && (
                    <Chip label={blogData?.status} color="success" />
                  )}
                  {blogData?.status === "draft" && !blogData?.scheduleBlogStatus && (
                    <Chip label={blogData?.status} color="warning" sx={{ backgroundColor: "#fff5eb", color: "#FE7E07" }} />

                  )}
                  {blogData?.scheduleBlogStatus && (
                    <>
                      <Chip label={"Scheduled"} color="warning" sx={{ backgroundColor: "#fffeebff", color: "#97fe07ff" }} />
                      {blogData?.scheduleDate && (
                        <Typography variant="body2" color="text.secondary">
                          {dayjs(blogData?.scheduleDate).format("MMM DD, YYYY")}
                        </Typography>
                      )}
                      {
                        blogData?.scheduleTime && (
                          <Typography variant="body2" color="text.secondary">
                            {convertToAmPm(blogData?.scheduleTime)}
                          </Typography>
                        )
                      }
                    </>

                  )}
                </Stack>
                {
                  canEdit &&
                  <Link to={`/manages/blog/${blogId}/edit`} style={{ textDecoration: 'none' }} className="">
                    <Button
                      size="small"
                      variant='contained'
                      startIcon={<Edit />}
                    >
                      Edit
                    </Button>
                  </Link>
                }
              </Box>
            </Card>

            <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
              <Grid container spacing={3}>
                {[
                  { label: "Blog Title (H1)", value: blogData.title },
                  { label: "Slug", value: blogData.slug },
                  { label: "Blog Category", value: blogData.category?.map(cat => cat.categoryName).join(', ') },
                  { label: "Author", value: blogData?.author?.name || (typeof blogData?.author === 'string' ? blogData?.author : "-") },
                  { label: "State", value: blogData.state 
                    ? blogData.state.replace(/-/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
                    : blogData.state },
                  { label: "Read Time", value: `${blogData.readTime} Min` },
                ].map((field, i) => (
                  <Grid item xs={12} key={i}>
                    <Typography variant="h6" fontWeight={500} color={grey[600]} mb={0.5}>{field.label}</Typography>
                    <Typography variant="h5" fontWeight={500}>{Array.isArray(field.value)
                      ? field.value.length > 0
                        ? field.value.join(', ')
                        : "-"
                      : field.value || "-"}</Typography>
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={500} color={grey[600]} mb={0.5}>Excerpt</Typography>
                  <Typography variant="h5" fontWeight={500}>{blogData.excerpt || "No excerpt available"}</Typography>
                </Grid>


                {/* <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={500} color={grey[600]} mb={0.5}>Content</Typography>
                  <Typography variant="h5" fontWeight={500}
                    dangerouslySetInnerHTML={{
                      __html: blogData.content || "-"
                    }}
                  />
                </Grid> */}
                <Grid item xs={12} className="blog-editor-content">
                  <Typography variant="h6" fontWeight={500} color={grey[600]} mb={0.5}>Content</Typography>
                  <Typography variant="h5" fontWeight={500}>
                    {(() => {
                      // If content is an array
                      if (Array.isArray(blogData.content)) {
                        return (
                          <div>
                            {blogData?.content.map((block, index) => {
                              // Handle different block types based on your data structure
                              if (typeof block === 'object') {
                                // If block has HTML content
                                if (block.html) {
                                  return <div key={index} dangerouslySetInnerHTML={{ __html: block.html }} />;
                                }
                                // If block has text content
                                if (block.text) {
                                  return <div key={index}>{block.text}</div>;
                                }
                                // If block has children/content array
                                if (block.content || block.children) {
                                  const innerContent = block.content || block.children;
                                  if (Array.isArray(innerContent)) {
                                    return innerContent.map((item, idx) => (
                                      <div key={`${index}-${idx}`}>
                                        {item.text || item.html || JSON.stringify(item)}
                                      </div>
                                    ));
                                  }
                                }
                                // Fallback for unknown object structure
                                return <div key={index}>{JSON.stringify(block)}</div>;
                              }
                              // If block is a string
                              return <div key={index} dangerouslySetInnerHTML={{ __html: block }} />;
                            })}
                          </div>
                        );
                      }
                      // If content is a string, render as HTML
                      if (typeof blogData.content === 'string') {
                        return <div dangerouslySetInnerHTML={{ __html: blogData.content || "-" }} />;
                      }
                      // If content is an object (not array)
                      if (typeof blogData.content === 'object' && blogData.content !== null) {
                        if (blogData.content.html) {
                          return <div dangerouslySetInnerHTML={{ __html: blogData.content.html }} />;
                        }
                        if (blogData.content.text) {
                          return <div>{blogData.content.text}</div>;
                        }
                        return <div>{JSON.stringify(blogData.content)}</div>;
                      }
                      // Default fallback
                      return <span>-</span>;
                    })()}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight={500} color={grey[600]} mb={0.5}>Banner For Website</Typography>
                  {blogData.banner ? (
                    <Avatar
                      src={blogData.banner}
                      variant="rounded"
                      sx={{ width: 120, height: 120, mt: 1 }}
                    />
                  ) : (
                    <Typography variant="body2" color="secondary">No banner available</Typography>
                  )}
                </Grid>

              </Grid>
            </Card>

            <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
              <Typography variant="h5" fontWeight={600} mb={3}>Meta Properties</Typography>

              <Grid container spacing={3}>
                {[
                  { label: "Meta Title", value: blogData.metaTitle },
                  // { label: "Updated By", value: blogData.updatedBy },
                  { label: "Description", value: blogData.metaDescription }
                ].map((field, i) => (
                  <Grid item xs={12} key={i}>
                    <Typography variant="h6" fontWeight={500} color={grey[600]} mb={0.5}>{field.label}</Typography>
                    <Typography variant="h5" fontWeight={500}>{field.value || "-"}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Card>

            <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight={600}>FAQs</Typography>
                <StatusText status={blogData?.faqStatus ? "active" : "inactive"} />
              </Box>

              <Box mt={3}>
                {blogData.faqs?.length > 0 ? (
                  blogData.faqs.map((faq, idx) => (
                    <Box key={idx} mb={2}>
                      <Typography variant="h6" fontWeight={500}
                        dangerouslySetInnerHTML={{
                          __html: faq.question || "-",
                        }} />
                      <Typography variant="h6" fontWeight={500} color={grey[600]}
                        dangerouslySetInnerHTML={{
                          __html: faq.answer || "-",
                        }} />
                      {idx < blogData.faqs.length - 1 && <Divider sx={{ my: 2 }} />}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color={grey[500]}>No FAQs available</Typography>
                )}
              </Box>


            </Card>
            <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight={600}>Relatable Blogs</Typography>
                <StatusText status={blogData?.relatedBlogStatus ? "active" : "inactive"} />
              </Box>

              <Box mt={3}>
                {blogData?.relatedBlogData?.length > 0 ? (
                  blogData?.relatedBlogData?.map((data, idx) => (
                    <Box key={idx} mb={2}>
                      <Typography variant="h6" fontWeight={500}
                      >{data.title} </Typography>
                      {idx < blogData?.relatedBlogData?.length - 1 && <Divider sx={{ my: 2 }} />}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color={grey[500]}>No Blogs available</Typography>
                )}
              </Box>


            </Card>
          </>
        )}
      </Box>
    </CommonLayout>

  );
}
